import type { ProjectClientRef, ProjectStatus } from '@fos/shared/contracts/projects';
import { PROJECT_BUDGET_WARNING_RATIO, projectStatusDisplay } from '@fos/shared/labels';
import { cn } from '../../../lib/cn';
import { Mono } from '../../../ui/Mono';
import { StatusPill } from '../../../ui/StatusPill';
import { projectHoursText } from '../project-format';

/** Kropka koloru klienta (8 px, dane klienta). Projekt wewnętrzny: ciemna; bez koloru: neutralna. */
export function ClientDot({
  client,
  internal = false,
  className,
}: {
  client: Pick<ProjectClientRef, 'color'> | null;
  internal?: boolean;
  className?: string;
}) {
  const color = client?.color ?? null;
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        !color && (internal ? 'bg-fg' : 'bg-line-strong'),
        className,
      )}
      style={color ? { backgroundColor: color } : undefined}
    />
  );
}

/** Status projektu do wyświetlenia (archiwum, budżet, „Nierozliczany”, status). */
export function ProjectStatusLabel({
  project,
  className,
}: {
  project: {
    status: ProjectStatus;
    isInternal: boolean;
    billableDefault: boolean;
    archivedAt: Date | string | null;
    hoursBudgetSeconds: number | null;
    usedSeconds: number;
  };
  className?: string;
}) {
  const display = projectStatusDisplay(project);
  return (
    <StatusPill tone={display.tone} className={className}>
      {display.label}
    </StatusPill>
  );
}

/**
 * Godziny projektu (Figma 07 Projekty, kolumna „Godziny”): pasek 110 × 4 px i „32 / 40 h” w foncie mono.
 * Bez budżetu tylko liczba godzin (wyciszona), bez czasu i budżetu „—”.
 */
export function ProjectHours({
  usedSeconds,
  budgetSeconds,
  className,
}: {
  usedSeconds: number;
  budgetSeconds: number | null;
  className?: string;
}) {
  const text = projectHoursText(usedSeconds, budgetSeconds);
  if (!budgetSeconds) {
    return <Mono className={cn('text-caption text-fg-muted', className)}>{text ?? '—'}</Mono>;
  }
  const ratio = usedSeconds / budgetSeconds;
  const fill = ratio > 1 ? 'bg-danger' : ratio >= PROJECT_BUDGET_WARNING_RATIO ? 'bg-warning' : 'bg-accent';
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <span
        role="progressbar"
        aria-label="Wykorzystanie budżetu godzin"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(Math.min(1, ratio) * 100)}
        className="h-1 w-[110px] shrink-0 overflow-hidden rounded-[2px] bg-elevated"
      >
        <span className={cn('block h-full rounded-[2px]', fill)} style={{ width: `${Math.min(1, ratio) * 100}%` }} />
      </span>
      <Mono className="text-caption whitespace-nowrap text-fg-secondary">{text}</Mono>
    </span>
  );
}
